import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import LocalizedFlatpickr from "components/Common/LocalizedFlatpickr"
import {
  fetchPartnerDetails,
  fetchPartnerEarnings,
  fetchPartnerOrders,
  getPartnerRatingsThunk,
  selectEarnings,
  selectPartnerLoading,
  selectPartnerOrders,
  selectPartnerOrdersPagination,
  selectRatings,
  selectSelectedPartner,
  verifyDocument,
} from "store/slices/deliveryPartnerSlice"
import DocumentVerificationModal from "./DocumentVerificationModal"
import EarningsChart from "./EarningsChart"
import CODSettlementCard from "./CODSettlementCard"
import RatingsSection from "./RatingsSection"
import LoadingSpinner from "./LoadingSpinner"
import Paginations from "components/Common/Paginations"
import formatDateTime from "components/Shared/formatDateTime"
import { getPartnerBranchName, getPartnerProfilePhoto } from "./branchUtils"
import { formatCurrencyAmount } from "utils/currency"
import { canViewRiderProfileTab } from "utils/riderProfileTabPermissions"
import { getRiderAvailabilityDisplay } from "utils/riderAvailability"
import {
  getSystemOrderStatusLabel,
  getSystemStatusDisplay,
  getSystemStatusFilterOptions,
} from "utils/orderStatusUtils"
import { Link } from "react-router-dom"

const normalizeStatusKey = status => {
  if (status === null || status === undefined) return ""
  return String(status).toLowerCase()
}

const partnerStatusColor = status => {
  switch (normalizeStatusKey(status)) {
    case "approved":
      return "success"
    case "pending":
      return "warning"
    case "suspended":
      return "danger"
    case "rejected":
      return "secondary"
    default:
      return "light"
  }
}

const deliveryStatusColor = status => {
  switch (normalizeStatusKey(status)) {
    case "assigned":
    case "created":
      return "primary"
    case "accepted":
      return "info"
    case "unassigned":
      return "secondary"
    case "in_progress":
    case "arrivedpickup":
    case "pickedup":
    case "outfordelivery":
    case "arriveddrop":
      return "warning"
    case "delivered":
      return "success"
    case "cancelled":
    case "failed":
      return "danger"
    default:
      return "light"
  }
}

const docStatusColor = status => {
  switch ((status || "").toLowerCase()) {
    case "approved":
    case "verified":
      return "success"
    case "rejected":
      return "danger"
    case "pending":
      return "warning"
    default:
      return "secondary"
  }
}

const DOCUMENT_TYPE_KEYS = [
  "Govt ID",
  "PAN Card",
  "Driving License",
  "Vehicle RC",
]

const translateStatus = (status, t) => {
  if (status === null || status === undefined || status === "") return t("Unknown")
  if (typeof status !== "string") return String(status)
  const key = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  if (key === "Verified") return t("Verified")
  return t(key, { defaultValue: status })
}

const RIDER_PROGRESS_STATUSES = [
  "Created",
  "Assigned",
  "Accepted",
  "ArrivedPickup",
  "PickedUp",
  "OutForDelivery",
  "ArrivedDrop",
  "Delivered",
  "Failed",
  "Cancelled",
]

const translateDeliveryStatus = (status, t) => {
  const riderLabels = {
    Created: t("Created"),
    Assigned: t("Assigned"),
    Accepted: t("Accepted"),
    ArrivedPickup: t("Arrived at Branch"),
    PickedUp: t("Picked Up"),
    OutForDelivery: t("Out for Delivery"),
    ArrivedDrop: t("Arrived at Customer"),
    Delivered: t("Delivered"),
    Failed: t("Failed"),
    Cancelled: t("Cancelled"),
  }
  if (riderLabels[status]) return riderLabels[status]

  const labels = {
    unassigned: t("Not Assigned"),
    assigned: t("Assigned"),
    in_progress: t("In Progress"),
    delivered: t("Delivered"),
    cancelled: t("Cancelled"),
  }
  return labels[normalizeStatusKey(status)] || translateStatus(status, t)
}

const resolveDeliveryStatusValue = order => {
  if (order?.status && RIDER_PROGRESS_STATUSES.includes(order.status)) {
    return order.status
  }
  const deliveryStatusFallbacks = {
    assigned: "Assigned",
    in_progress: "Accepted",
    delivered: "Delivered",
    cancelled: "Cancelled",
    unassigned: "unassigned",
  }
  if (order?.deliveryStatus && deliveryStatusFallbacks[order.deliveryStatus]) {
    return deliveryStatusFallbacks[order.deliveryStatus]
  }
  return order?.deliveryStatus || null
}

// Created (rider rejected) and Cancelled (order cancelled) both unassign the rider,
// so those orders never appear in a rider's list.
const RIDER_STATUS_FILTER_OPTIONS = RIDER_PROGRESS_STATUSES.filter(
  status => status !== "Created" && status !== "Cancelled"
)

const EMPTY_ORDER_FILTERS = {
  orderStatus: "",
  riderStatus: "",
  orderNo: "",
  dateRange: null,
}

const ORDER_NO_SEARCH_DELAY_MS = 400

// Same single-calendar range picker as the Orders screen. Flatpickr renders
// the calendar on <body>, so the modal's overflow can't clip it.
const ORDER_DATE_RANGE_OPTIONS = { mode: "range", dateFormat: "Y-m-d" }
// Stable empty value: a new [] each render would make Flatpickr clear a
// half-picked range on any re-render.
const EMPTY_DATE_RANGE = []

const toOrderDateRange = range => {
  const [from, to] = range || []
  if (!from || !to) return undefined
  const min = new Date(from)
  min.setHours(0, 0, 0, 0)
  const max = new Date(to)
  max.setHours(23, 59, 59, 999)
  return { min: min.toISOString(), max: max.toISOString() }
}

const TAB_DEFINITIONS = [
  { id: "overview", labelKey: "Overview" },
  { id: "statusHistory", labelKey: "Status History" },
  { id: "documents", labelKey: "Documents" },
  { id: "orders", labelKey: "Orders" },
  { id: "earnings", labelKey: "Earnings" },
  { id: "payments", labelKey: "Payments" },
  { id: "ratings", labelKey: "Ratings" },
  { id: "location", labelKey: "Location" },
]

const DeliveryPartnerDetails = ({
  partnerId,
  initialTab = "overview",
  permissions = {},
  profileSubPermissions = {},
  onSettleCOD,
  codData,
  codLoading,
}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const partner = useSelector(selectSelectedPartner)
  const loading = useSelector(selectPartnerLoading)
  const earnings = useSelector(selectEarnings)
  const ratings = useSelector(selectRatings)
  const orders = useSelector(selectPartnerOrders)
  const ordersPagination = useSelector(selectPartnerOrdersPagination)

  const canVerifyDocuments = Boolean(permissions.documents)
  const canViewEarnings = Boolean(permissions.earnings)
  const canViewBank = Boolean(permissions.bank)

  const tabPermissionContext = useMemo(
    () => ({
      hasView: Boolean(permissions.view),
      subPermissions: profileSubPermissions,
      legacyPermissions: permissions,
    }),
    [permissions, profileSubPermissions]
  )

  const canViewTab = useCallback(
    tabId => canViewRiderProfileTab({ ...tabPermissionContext, tabId }),
    [tabPermissionContext]
  )

  const [activeTab, setActiveTab] = useState("overview")
  const [docModal, setDocModal] = useState({ isOpen: false, document: null })
  const [ratingsPage, setRatingsPage] = useState(1)
  const [ordersPage, setOrdersPage] = useState(1)
  const [ordersFilters, setOrdersFilters] = useState(EMPTY_ORDER_FILTERS)
  const [orderNoInput, setOrderNoInput] = useState("")

  const ordersRequestFilters = useMemo(
    () => ({
      orderStatus: ordersFilters.orderStatus || undefined,
      riderStatus: ordersFilters.riderStatus || undefined,
      orderNo: ordersFilters.orderNo || undefined,
      dateRange: toOrderDateRange(ordersFilters.dateRange),
    }),
    [ordersFilters]
  )

  const hasOrderFilters =
    Boolean(orderNoInput.trim()) ||
    Object.values(ordersRequestFilters).some(Boolean)

  // Riders only get delivery orders, so show the delivery name for each
  // status (3 = Out for Delivery, 4 = Delivered) instead of the combined
  // delivery / pickup labels.
  const orderStatusOptions = useMemo(
    () =>
      getSystemStatusFilterOptions(t).map(option => ({
        label:
          option.value === 0
            ? option.label
            : getSystemOrderStatusLabel(option.value, "delivery", t),
        value: option.value === 0 ? "" : String(option.value),
      })),
    [t]
  )

  const tabs = useMemo(
    () => TAB_DEFINITIONS.filter(tab => canViewTab(tab.id)),
    [canViewTab]
  )

  useEffect(() => {
    if (!tabs.some(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id || "overview")
    }
  }, [tabs, activeTab])

  useEffect(() => {
    if (initialTab && tabs.some(tab => tab.id === initialTab)) {
      setActiveTab(initialTab)
      if (initialTab === "orders") {
        setOrdersPage(1)
      }
    }
  }, [initialTab, partnerId, tabs])

  useEffect(() => {
    if (partnerId) {
      dispatch(fetchPartnerDetails(partnerId))
      if (canViewTab("earnings") && canViewEarnings) {
        dispatch(fetchPartnerEarnings({ id: partnerId, period: "daily" }))
      }
      if (canViewTab("ratings")) {
        dispatch(getPartnerRatingsThunk({ id: partnerId, page: 1 }))
      }
      setOrdersPage(1)
      setOrdersFilters(EMPTY_ORDER_FILTERS)
      setOrderNoInput("")
    }
  }, [partnerId, dispatch, canViewTab, canViewEarnings])

  useEffect(() => {
    const orderNo = orderNoInput.trim()
    if (orderNo === ordersFilters.orderNo) return undefined
    const timer = setTimeout(() => {
      setOrdersFilters(prev => ({ ...prev, orderNo }))
      setOrdersPage(1)
    }, ORDER_NO_SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [orderNoInput, ordersFilters.orderNo])

  useEffect(() => {
    if (activeTab === "orders" && partnerId && canViewTab("orders")) {
      dispatch(
        fetchPartnerOrders({
          id: partnerId,
          page: ordersPage,
          filters: ordersRequestFilters,
        })
      )
    }
  }, [activeTab, partnerId, ordersPage, ordersRequestFilters, dispatch, canViewTab])

  const handlePeriodChange = period => {
    if (!canViewEarnings || !canViewTab("earnings")) return
    dispatch(fetchPartnerEarnings({ id: partnerId, period }))
  }

  const handleRatingsPageChange = page => {
    if (!canViewTab("ratings")) return
    setRatingsPage(page)
    dispatch(getPartnerRatingsThunk({ id: partnerId, page }))
  }

  const handleOrdersPageChange = page => {
    setOrdersPage(page)
  }

  const handleOrdersFilterChange = (field, value) => {
    setOrdersFilters(prev => ({ ...prev, [field]: value }))
    setOrdersPage(1)
  }

  const handleOrdersFiltersReset = () => {
    setOrdersFilters(EMPTY_ORDER_FILTERS)
    setOrderNoInput("")
    setOrdersPage(1)
  }

  const handleTabChange = tabId => {
    if (!canViewTab(tabId)) return
    setActiveTab(tabId)
    if (tabId === "orders") {
      setOrdersPage(1)
    }
  }

  const handleDocApprove = doc => {
    if (!canVerifyDocuments) return
    dispatch(
      verifyDocument({
        partnerId,
        documentId: doc._id || doc.id,
        status: "approved",
        reason: "",
      })
    )
    setDocModal({ isOpen: false, document: null })
  }

  const handleDocReject = (doc, reason) => {
    if (!canVerifyDocuments) return
    dispatch(
      verifyDocument({
        partnerId,
        documentId: doc._id || doc.id,
        status: "rejected",
        reason,
      })
    )
    setDocModal({ isOpen: false, document: null })
  }

  if (loading.details) return <LoadingSpinner text={t("Loading rider details...")} />
  if (!partner) return null

  const documents = partner.documents || DOCUMENT_TYPE_KEYS.map(type => ({ type, status: "pending" }))
  const bankDetails = partner.bankDetails || {}
  const branchName = getPartnerBranchName(partner, i18n.language)
  const profilePhoto = getPartnerProfilePhoto(partner)
  const statusHistory = Array.isArray(partner.statusHistory) ? partner.statusHistory : []

  const showEarningsKpis = canViewTab("earnings") && canViewEarnings

  const kpiCards = [
    { labelKey: "Total Deliveries", value: partner.totalDeliveries ?? 0, icon: "bx-package" },
    {
      labelKey: "Avg Rating",
      value: `${Number(partner.averageRating ?? 0).toFixed(1)} ★`,
      icon: "bx-star",
    },
    ...(showEarningsKpis
      ? [
          {
            labelKey: "Total Earnings",
            value: formatCurrencyAmount(partner.totalEarnings ?? 0),
            icon: "bx-money",
          },
          {
            labelKey: "This Month",
            value: formatCurrencyAmount(partner.monthlyEarnings ?? 0),
            icon: "bx-calendar",
          },
        ]
      : []),
  ]

  return (
    <div>
      <Card className="shadow-sm mb-3">
        <CardBody>
          <Row className="align-items-center">
            <Col xs="auto">
              <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                style={{ width: 72, height: 72, overflow: "hidden" }}
              >
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt={partner.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <i className="bx bx-user font-size-36 text-muted"></i>
                )}
              </div>
            </Col>
            <Col>
              <h5 className="mb-1">{partner.name || t("N/A")}</h5>
              <p className="mb-1 text-muted">
                <i className="bx bx-phone me-1"></i>
                {partner.phone ? (
                  <span
                    dir="ltr"
                    style={{ unicodeBidi: "bidi-override" }}
                  >
                    {partner.phone}
                  </span>
                ) : (
                  t("N/A")
                )}
              </p>
              <p className="mb-1 text-muted">
                <i className="bx bx-envelope me-1"></i>
                {partner.email || t("N/A")}
              </p>
              <p className="mb-0 text-muted">
                <i className="bx bx-store me-1"></i>
                {branchName || t("N/A")}
              </p>
            </Col>
            <Col xs="auto" className="d-flex flex-column align-items-end gap-2">
              <Badge color={partnerStatusColor(partner.status)} className="font-size-13 px-3 py-2">
                {translateStatus(partner.status, t)}
              </Badge>
              {(() => {
                const availability = getRiderAvailabilityDisplay(partner, t)
                return (
                  <span className="d-inline-flex align-items-center gap-1 small">
                    <span
                      className="rounded-circle flex-shrink-0"
                      style={{
                        width: 8,
                        height: 8,
                        backgroundColor: availability.color,
                      }}
                    />
                    <span className={availability.textClass}>
                      {availability.label}
                    </span>
                  </span>
                )
              })()}
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Row className="g-3 mb-3">
        {kpiCards.map(kpi => (
          <Col xs={6} md={3} key={kpi.labelKey}>
            <Card className="shadow-sm text-center h-100">
              <CardBody>
                <i className={`bx ${kpi.icon} font-size-28 text-primary mb-2 d-block`}></i>
                <h5 className="mb-1">{kpi.value}</h5>
                <small className="text-muted">{t(kpi.labelKey)}</small>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="shadow-sm">
        <CardBody>
          <Nav tabs className="mb-3">
            {tabs.map(tab => (
              <NavItem key={tab.id}>
                <NavLink
                  className={activeTab === tab.id ? "active" : ""}
                  onClick={() => handleTabChange(tab.id)}
                  style={{ cursor: "pointer" }}
                >
                  {t(tab.labelKey)}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="overview">
              <Row>
                <Col md={6}>
                  <table className="table table-borderless table-sm">
                    <tbody>
                      <tr>
                        <th className="text-muted fw-normal">{t("Branch")}</th>
                        <td>{branchName || t("N/A")}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Address")}</th>
                        <td>{partner.address || t("N/A")}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Joining Date")}</th>
                        <td>
                          {partner.createdAt
                            ? new Date(partner.createdAt).toLocaleDateString()
                            : t("N/A")}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Vehicle Type")}</th>
                        <td>{partner.vehicleType || t("N/A")}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Vehicle Number")}</th>
                        <td>{partner.vehicleNumber || t("N/A")}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Last Active")}</th>
                        <td>
                          {partner.lastActive
                            ? new Date(partner.lastActive).toLocaleString()
                            : t("N/A")}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>
            </TabPane>

            <TabPane tabId="statusHistory">
              {statusHistory.length === 0 ? (
                <div className="text-center py-4 text-muted">{t("No status history found")}</div>
              ) : (
                <Table responsive hover>
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>{t("Status")}</th>
                      <th>{t("Reason")}</th>
                      <th>{t("Actor")}</th>
                      <th>{t("Date")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusHistory.map((entry, idx) => (
                      <tr key={entry._id || `${entry.status}-${entry.changedAt}-${idx}`}>
                        <td>{idx + 1}</td>
                        <td>
                          <Badge color={partnerStatusColor(entry.status)}>
                            {translateStatus(entry.status, t)}
                          </Badge>
                        </td>
                        <td>{entry.reason || "—"}</td>
                        <td>{entry.actorName || t("N/A")}</td>
                        <td>{entry.changedAt ? formatDateTime(entry.changedAt) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </TabPane>

            <TabPane tabId="documents">
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>{t("Document Type")}</th>
                    <th>{t("Status")}</th>
                    <th>{t("Uploaded")}</th>
                    {canVerifyDocuments ? <th>{t("Action")}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, idx) => (
                    <tr key={doc._id || idx}>
                      <td>
                        {t(doc.type || DOCUMENT_TYPE_KEYS[idx] || "Document", {
                          defaultValue: doc.type || DOCUMENT_TYPE_KEYS[idx] || t("Document"),
                        })}
                      </td>
                      <td>
                        <Badge color={docStatusColor(doc.status)}>
                          {translateStatus(doc.status, t)}
                        </Badge>
                      </td>
                      <td>
                        {doc.uploadedAt
                          ? new Date(doc.uploadedAt).toLocaleDateString()
                          : "—"}
                      </td>
                      {canVerifyDocuments ? (
                        <td>
                          <Button
                            size="sm"
                            color="primary"
                            outline
                            onClick={() => setDocModal({ isOpen: true, document: doc })}
                          >
                            {t("Verify")}
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TabPane>

            <TabPane tabId="orders">
              <Row className="g-2 mb-3 align-items-end">
                <Col md={3} sm={6}>
                  <Label className="form-label mb-1" htmlFor="riderOrdersOrderNo">
                    {t("Order Number")}
                  </Label>
                  <Input
                    id="riderOrdersOrderNo"
                    type="search"
                    maxLength={50}
                    value={orderNoInput}
                    placeholder={t("Search order number")}
                    onChange={e => setOrderNoInput(e.target.value)}
                  />
                </Col>
                <Col md={2} sm={6}>
                  <Label className="form-label mb-1" htmlFor="riderOrdersOrderStatus">
                    {t("Order Status")}
                  </Label>
                  <Input
                    id="riderOrdersOrderStatus"
                    type="select"
                    value={ordersFilters.orderStatus}
                    onChange={e => handleOrdersFilterChange("orderStatus", e.target.value)}
                  >
                    {orderStatusOptions.map(option => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={2} sm={6}>
                  <Label className="form-label mb-1" htmlFor="riderOrdersRiderStatus">
                    {t("Rider Status")}
                  </Label>
                  <Input
                    id="riderOrdersRiderStatus"
                    type="select"
                    value={ordersFilters.riderStatus}
                    onChange={e => handleOrdersFilterChange("riderStatus", e.target.value)}
                  >
                    <option value="">{t("All")}</option>
                    {RIDER_STATUS_FILTER_OPTIONS.map(status => (
                      <option key={status} value={status}>
                        {translateDeliveryStatus(status, t)}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3} sm={6}>
                  <Label className="form-label mb-1">{t("Date")}</Label>
                  <LocalizedFlatpickr
                    className="form-control d-block"
                    placeholder={t("Select date range")}
                    value={ordersFilters.dateRange || EMPTY_DATE_RANGE}
                    options={ORDER_DATE_RANGE_OPTIONS}
                    onChange={selectedDates => {
                      // Wait for the end date so a half-picked range doesn't refetch.
                      if (selectedDates.length === 1) return
                      handleOrdersFilterChange(
                        "dateRange",
                        selectedDates.length === 2 ? selectedDates : null
                      )
                    }}
                  />
                </Col>
                <Col md={2} sm={12} className="text-md-end">
                  <Button
                    color="light"
                    className="w-100"
                    disabled={!hasOrderFilters}
                    onClick={handleOrdersFiltersReset}
                  >
                    {t("Reset")}
                  </Button>
                </Col>
              </Row>
              {loading.orders ? (
                <div className="text-center py-4">
                  <LoadingSpinner text={t("Loading orders...")} />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  {hasOrderFilters
                    ? t("No orders match these filters")
                    : t("No orders found")}
                </div>
              ) : (
                <>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t("Order ID")}</th>
                        <th>{t("Date")}</th>
                        <th>{t("Amount")}</th>
                        <th>{t("Delivery Status")}</th>
                        <th>{t("Order Status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, idx) => {
                        const orderLabel =
                          order.invoiceNo || order.orderId || order._id
                        const orderStatusDisplay = getSystemStatusDisplay(order, t)
                        const deliveryStatusValue = resolveDeliveryStatusValue(order)
                        const rowNum =
                          (ordersPagination.currentPage - 1) *
                            ordersPagination.itemsPerPage +
                          idx +
                          1
                        return (
                          <tr key={order._id || idx}>
                            <td>{rowNum}</td>
                            <td>
                              <Link
                                to={`/f&b-orders/order-details?id=${order._id}`}
                                className="text-primary"
                              >
                                {orderLabel}
                              </Link>
                            </td>
                            <td>
                              {order.createdAt
                                ? new Date(order.createdAt).toLocaleDateString()
                                : "—"}
                            </td>
                            <td>
                              {formatCurrencyAmount(
                                order.totalAmount ?? order.paidAmount ?? 0,
                                order.splitPayments?.currency
                              )}
                            </td>
                            <td>
                              <Badge color={deliveryStatusColor(deliveryStatusValue)}>
                                {translateDeliveryStatus(deliveryStatusValue, t)}
                              </Badge>
                            </td>
                            <td>
                              <span
                                style={{
                                  color: orderStatusDisplay.color,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {orderStatusDisplay.text}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                  {ordersPagination.totalPages > 1 ? (
                    <Paginations
                      currentPage={ordersPagination.currentPage}
                      totalPages={ordersPagination.totalPages}
                      totalCount={ordersPagination.totalCount}
                      onPageChange={handleOrdersPageChange}
                    />
                  ) : null}
                </>
              )}
            </TabPane>

            <TabPane tabId="earnings">
              <EarningsChart
                earnings={earnings}
                onPeriodChange={handlePeriodChange}
                loading={loading.earnings}
              />
            </TabPane>

            <TabPane tabId="payments">
              <Row>
                {canViewBank ? (
                  <Col md={canViewEarnings ? 6 : 12}>
                    <h6 className="fw-semibold mb-3">{t("Bank Details")}</h6>
                    <table className="table table-borderless table-sm mb-4">
                      <tbody>
                        <tr>
                          <th className="text-muted fw-normal">{t("Bank Name")}</th>
                          <td>{bankDetails.bankName || t("N/A")}</td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">{t("Account Number")}</th>
                          <td>{bankDetails.accountNumber || t("N/A")}</td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">{t("IFSC Code")}</th>
                          <td>{bankDetails.ifsc || t("N/A")}</td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">{t("Account Holder")}</th>
                          <td>{bankDetails.holderName || partner.name || t("N/A")}</td>
                        </tr>
                      </tbody>
                    </table>
                  </Col>
                ) : null}
                {canViewEarnings ? (
                  <Col md={canViewBank ? 6 : 12}>
                    <CODSettlementCard
                      codData={codData}
                      onSettle={onSettleCOD}
                      loading={codLoading}
                      canSettle
                    />
                  </Col>
                ) : null}
              </Row>
            </TabPane>

            <TabPane tabId="ratings">
              <RatingsSection
                ratings={ratings}
                averageRating={partner.averageRating ?? 0}
                currentPage={ratingsPage}
                totalPages={Math.ceil((partner.totalRatings ?? ratings.length) / 10)}
                onPageChange={handleRatingsPageChange}
              />
            </TabPane>
            <TabPane tabId="location">
              <div className="text-center py-4 text-muted">
                {t("Location tracking will be available here.")}
              </div>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>

      {canVerifyDocuments ? (
        <DocumentVerificationModal
          isOpen={docModal.isOpen}
          toggle={() => setDocModal({ isOpen: false, document: null })}
          document={docModal.document}
          onApprove={handleDocApprove}
          onReject={handleDocReject}
        />
      ) : null}
    </div>
  )
}

export default DeliveryPartnerDetails
